/* eslint-disable @typescript-eslint/max-params */
import { useMutation, useQuery } from '@tanstack/react-query'
import { intentSource } from 'lib/auth/config'
import api from 'lib/interceptor/axios.interceptor'
import type { ISupplierContact } from 'models/affiliateOrganizations'
import type {
	IContractClauses,
	IOrganization,
	ISourcingProfile,
	IVenueList,
	IataProfile,
	LicenseInfo,
	Subscription,
	SubscriptionInfo,
	VenueProperties
} from 'models/organizations'
import type { Member } from 'models/propertyPartner'
import type { EventPlan } from 'models/proposalResponseMonitor'
import type { Venue, VenueDetail } from 'models/venue'
import type { IOrganizationActionPaylod } from 'pages/organization/store/reducer'

const getOrganizations = async ({
	filter,
	top,
	skip,
	orderBy
}: {
	filter: string
	top: string
	skip: string
	orderBy: { field: string; direction: number }
}) => {
	const response = await api.get(
		`/api/organizations?$filter=${filter}&$offset=${skip}&$limit=${top}&$orderby=${orderBy.field}+${orderBy.direction < 0 ? 'desc' : 'asc'}`
	)
	return response.data as IOrganization[]
}

export const getSourcingProfiles = async (organizationId: string) => {
	const response = await api.get(
		`/api/organizations/${organizationId}/sourcingProfiles`
	)
	return response.data as ISourcingProfile[]
}

export const loadOrganizationById = async (organizationId: string) => {
	const response = await api.get(`/api/organizations/${organizationId || 'me'}`)
	return response.data as IOrganization
}

export const getMembers = async (organizationId: string) => {
	const response = await api.get(`/api/organizations/${organizationId}/members`)
	return response.data as Member[]
}

export const removeMember = async (
	organizationId: string,
	memberId: string
) => {
	const response = await api.delete(
		`/api/organizations/${organizationId}/members/${memberId}`
	)
	return response.data as Member[]
}

export const addSourcingProfile = async (
	organizationId: string,
	sourcingProfile: ISourcingProfile
) => {
	const response = await api.post(
		`/api/organizations/${organizationId}/sourcingProfiles/`,
		{
			...sourcingProfile
		}
	)
	return response.data as ISourcingProfile
}

export const updateSourcingProfile = async (
	organizationId: string,
	sourcingProfileId: string,
	sourcingProfile: ISourcingProfile
) => {
	const response = await api.put(
		`/api/organizations/${organizationId}/sourcingProfiles/${sourcingProfileId}`,
		{
			...sourcingProfile
		}
	)
	return response.data as ISourcingProfile
}

export const useGetOrganizations = (
	{
		filter,
		top,
		skip,
		orderBy
	}: {
		filter: string
		top: string
		skip: string
		orderBy: { field: string; direction: number }
	},
	enabled: boolean
) =>
	useQuery({
		queryKey: ['organizations', filter, top, skip, orderBy],
		queryFn: async () => getOrganizations({ filter, top, skip, orderBy }),
		enabled
	})

export const useGetOrganizationById = (
	organizationId: string,
	enabled = true
) =>
	useQuery({
		queryKey: ['organization', organizationId],
		queryFn: async () => loadOrganizationById(organizationId),
		enabled
	})

export const useGetSourcingProfiles = (
	organizationId: string,
	enabled = true
) =>
	useQuery({
		queryKey: ['organizations', organizationId],
		queryFn: async () => getSourcingProfiles(organizationId),
		enabled
	})

export const useUpsertSourcingProfile = () =>
	useMutation({
		mutationKey: ['upsertSourcingProfile'],
		mutationFn: async ({
			organizationId,
			sourcingProfileId,
			sourcingProfile
		}: {
			organizationId: string
			sourcingProfileId: string
			sourcingProfile: ISourcingProfile
		}) => {
			if (sourcingProfileId) {
				return updateSourcingProfile(
					organizationId,
					sourcingProfileId,
					sourcingProfile
				)
			}
			return addSourcingProfile(organizationId, sourcingProfile)
		}
	})

export const useGetMembersForOrganization = (
	organizationId: string,
	enabled = true
) =>
	useQuery({
		queryKey: ['members', organizationId],
		queryFn: async () => getMembers(organizationId),
		enabled
	})

export const loadSubscriptions = async (organizationId: string) => {
	const response = await api.get(
		`/api/organizations/${organizationId}/subscriptions`
	)
	return response.data as Subscription[]
}

export const useLoadSubscriptions = (organizationId: string) =>
	useQuery({
		queryKey: ['subscriptions', organizationId],
		queryFn: async () => loadSubscriptions(organizationId),
		enabled: !!organizationId
	})

export const sortMembers = (c: Member, n: Member) => {
	const currentFirstName = c.firstName ?? ''
	const currentLastName = c.lastName ?? ''
	const nextFirstName = n.firstName ?? ''
	const nextLastName = n.lastName ?? ''
	const currentId = c.id ?? ''
	const nextId = n.id ?? ''
	if (currentFirstName === nextFirstName) {
		if (currentLastName === nextLastName) {
			return currentId > nextId ? 1 : -1
		}
		return currentLastName > nextLastName ? 1 : -1
	}
	return currentFirstName > nextFirstName ? 1 : -1
}

export const addMemberToOrganization = async (
	organizationId: string,
	emails: string[]
) => {
	const response = await api.post(
		`/api/organizations/${organizationId}/members`,
		emails.map(email => ({ memberId: email }))
	)
	return response.data as Member[]
}

export const loadSupplierContacts = async (organizationId: string) => {
	const response = await api.get(
		`/api/organizations/${organizationId}/contacts`
	)
	return response.data as ISupplierContact[]
}

export const updateSupplierContact = async (
	organizationId: string,
	supplierContact: ISupplierContact,
	properties: Partial<ISupplierContact>
) => {
	const response = await api.put(
		`/api/organizations/${organizationId}/contacts/${supplierContact.id}`,
		{ ...supplierContact, ...properties }
	)
	return response.data as ISupplierContact[]
}

export const useLoadSupplierContacts = (
	organizationId: string,
	enabled?: boolean
) =>
	useQuery({
		queryKey: ['contacts', organizationId],
		queryFn: async () => loadSupplierContacts(organizationId),
		enabled
	})

export const deleteSupplierContact = async (
	organizationId: string,
	item: ISupplierContact
) => api.delete(`/api/organizations/${organizationId}/contacts/${item.id}`)

export const saveSupplierContact = async (data: {
	organizationId: string
	contact: ISupplierContact
	isEdit?: boolean
	isRFP?: boolean
	defaultInvitationMessage: string | null
}) => {
	const url = `/api/organizations/${data.organizationId}/contacts${data.isEdit ? `/${data.contact.id}` : ''}`
	const payload: ISupplierContact = {
		...data.contact,
		invitationMessage:
			data.isRFP && data.isEdit
				? null
				: (data.contact.invitationMessage ?? data.defaultInvitationMessage)
	}

	const response = await api.request({
		url,
		method: data.isEdit ? 'PUT' : 'POST',
		data: payload
	})

	return response.data as ISupplierContact[]
}

export const loadImages = async (organizationId: string) => {
	const response = await api.get(`/api/organizations/${organizationId}/images`)
	return response.data as string[]
}

export const useLoadImages = (organizationId: string) =>
	useQuery({
		queryKey: ['images', organizationId],
		queryFn: async () => loadImages(organizationId)
	})

export const removeImage = async (destinationId: string, imageId: string) => {
	const response = await api.delete(
		`/api/organizations/${destinationId}/images/${imageId}`
	)
	return response.data as []
}

export const uploadImage = async (
	organizationId: string,
	imageId: string,
	formData: FormData
) => {
	await api.post(
		`/api/organizations/${organizationId}/images/${imageId}`,
		formData
	)
}

export const onRequestAdminAccess = async (organizationId: string) => {
	await api.post(`/api/organizations/${organizationId}/requestAdminAccess`)
}

export const verifyIATA = async (
	organizationId: string,
	agencyName: string,
	verifyItemId: string
) => {
	const response = await api.post(
		`/api/organizations/${organizationId.toLowerCase()}/verifyiata/${verifyItemId}`,

		{ agencyName }
	)

	return response.data as IataProfile[]
}

export const updateOrganization = async (
	organizationId: string,
	payload: IOrganizationActionPaylod
) => {
	const response = await api.put(
		`/api/organizations/${organizationId}/intent`,
		{ ...payload, source: intentSource }
	)

	return response.data as IOrganization
}

export const deleteSourcingProfile = async (
	organizationId: string,
	sourcingProfileId: string
) =>
	api.delete(
		`/api/organizations/${organizationId}/sourcingProfiles/${sourcingProfileId}`
	)

export const upsertSourcingProfile = async (
	organizationId: string,
	sourcingProfileId: string,
	sourcingProfile: ISourcingProfile
) =>
	sourcingProfileId
		? updateSourcingProfile(organizationId, sourcingProfileId, sourcingProfile)
		: addSourcingProfile(organizationId, sourcingProfile)

export const loadContractClauses = async (organizationId: string) => {
	const response = await api.get(`/api/organizations/${organizationId}/clauses`)
	return response.data as IContractClauses[]
}

export const useLoadContractClauses = (
	organizationId: string,
	enabled?: boolean
) =>
	useQuery({
		queryKey: ['clauses', organizationId],
		queryFn: async () => loadContractClauses(organizationId),
		enabled
	})

export const deleteContractClause = async (
	organizationId: string,
	clauseId: string
) => {
	const response = await api.delete(
		`/api/organizations/${organizationId}/clauses/${clauseId}`
	)
	return response.data as IContractClauses
}

export const updateContractClause = async (
	organizationId: string,
	contractClause: IContractClauses,
	properties?: Partial<IContractClauses>
) => {
	const response = await api.post(
		`/api/organizations/${organizationId}/clauses`,
		{
			...contractClause,
			...properties
		}
	)

	return response.data as IContractClauses
}

export const updateDeclinedReasons = async (
	organizationId: string,
	declinedReasons: {
		text: string
		disabled: boolean
		created: Date
		createdBy: string
	}[]
) => {
	const response = await api.put(
		`/api/organizations/${organizationId}/intent`,
		{
			action: { type: 'setProperty' },
			value: {
				name: 'declinedReasons',
				value: declinedReasons
			},
			source: intentSource
		}
	)
	return response.data as IOrganization
}

export const loadDeclinedReasonsUsage = async (organizationId: string) => {
	const response = await api.get(
		`/api/organizations/${organizationId}/declinedReasons`
	)

	return response.data as Record<string, number>
}

export const useLoadDeclinedReasons = (
	organizationId: string,
	enabled: boolean
) =>
	useQuery({
		queryKey: ['declineReasons', organizationId],
		queryFn: async () => loadDeclinedReasonsUsage(organizationId),
		enabled
	})

export const loadEventPlans = async (organizationId: string) => {
	const response = await api.get(
		`/api/organizations/${organizationId}/eventplans`
	)
	return response.data as EventPlan[]
}

export const useLoadEventPlans = (organizationId: string, enabled = true) =>
	useQuery({
		queryKey: ['event-plans', organizationId],
		queryFn: async () => loadEventPlans(organizationId),
		enabled
	})

export const getVenueLists = async (organizationId: string) => {
	const response = await api.get(
		`/sourcing/api/organizations/${organizationId}/venueLists`
	)
	return response.data as IVenueList[]
}

export const useGetVenueList = (organizationId: string, enabled: boolean) =>
	useQuery({
		queryKey: ['venueList', organizationId],
		queryFn: async () => getVenueLists(organizationId),
		enabled
	})

export const deleteVenueList = async (
	organizationId: string,
	venueListId: string
) =>
	api.delete(
		`/sourcing/api/organizations/${organizationId}/venueLists/${venueListId}`
	)

export const mapVenueToAssociatedObject = (
	venue: Venue,
	properties: Partial<VenueProperties>
): VenueDetail => ({
	id: venue.id,
	partitionKey: venue.id ?? '',
	name: venue.name,
	itemType: venue.itemType ?? 'hotel.property',
	properties: {
		address: venue.address,
		city: venue.city,
		state: venue.state,
		zip: venue.zip,
		country: venue.country,
		chainId: venue.chainId,
		brandId: venue.brandId,
		geolocation: venue.geolocation,
		guestRoomQuantity: venue.guestRoomQuantity,
		meetingRoomQuantity: venue.meetingRoomQuantity,
		meetingSpaceSquareFeet: venue.meetingSpaceSquareFeet,
		largestMeetingSpaceSquareFeet: venue.largestMeetingSpaceSquareFeet,
		...properties
	}
})

export const updateVenueList = async (
	organizationId: string,
	venueListId: string,
	venueList: IVenueList
) => {
	const response = await api.put(
		`/sourcing/api/organizations/${organizationId}/venueLists/${venueListId}`,
		{ ...venueList }
	)
	return response.data as IVenueList
}

export interface VenueListPayload {
	userProfileId: string
	venues: VenueDetail[]
	name: string
	description: string
	isShared: boolean
	sharedWith?: {
		id: string
		partitionKey: string
		name: string
		itemType: string
	}[]
}

export const addVenueList = async (
	organizationId: string,
	venueList: VenueListPayload
) => {
	const response = await api.post(
		`/sourcing/api/organizations/${organizationId}/venueLists`,
		{
			...venueList
		}
	)
	return response.data as IVenueList
}

export const upsertVenueList = async (
	organizationId: string,
	venueListId: string,
	venueList: IVenueList
) => {
	if (venueListId) {
		return updateVenueList(organizationId, venueListId, venueList)
	}
	return addVenueList(organizationId, venueList)
}

export const onSubscriptionAdded = async (
	organizationId: string,
	subscription: SubscriptionInfo,
	licenseCount: number
) =>
	api.post(`/api/organizations/${organizationId}/subscriptions`, {
		subscriptionInfo: { ...subscription },
		licenseCount
	})

export const onSubscriptionUpdated = async (
	organizationId: string,
	subscription: SubscriptionInfo,
	licenseInfos: LicenseInfo[]
) =>
	api.put(
		`/api/organizations/${organizationId}/subscriptions/${subscription.id}`,
		{
			subscriptionInfo: { ...subscription },
			licenseInfos
		}
	)

export const updateMemberRole = async (
	organizationId: string,
	member: Member,
	roleName: string,
	isInRole: boolean
) => {
	const r = await api.put(
		`/api/organizations/${organizationId}/members/${member.id}`,
		{ roleName, isInRole }
	)
	return r
}

export const assignSubscriptionToMember = async (
	organizationId: string,
	subscriptionInfoId: string,
	memberId: string,
	payload: { assignLicense: true }
) => {
	const r = await api.post(
		`/api/organizations/${organizationId}/subscriptions/${subscriptionInfoId}/${encodeURIComponent(memberId)}`,
		payload
	)
	return r
}
